import { uiText, useUiLanguage } from '@/i18n/interface';
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { toast } from "@/hooks/use-toast";
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { useStudentDeletion } from '../../hooks/useStudentDeletion';
import StudentSelector from './StudentSelector';
import StudentFormFields from './StudentFormFields';
import StudentActions from './StudentActions';
import StudentDetailsView from './StudentDetailsView';
import AddManagedStudentDialog from './AddManagedStudentDialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Trash2, Users } from 'lucide-react'; // Assuming Users icon is needed and imported
import { Button } from "@/components/ui/button";
import { useTranslation } from 'react-i18next'; // Assuming useTranslation is used
import StudentStatusToggle from '../StudentStatusToggle';


interface Student {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  phone?: string;
  address?: string;
  city?: string;
  postal_code?: string;
  date_of_birth?: string;
  belt_level_id?: string;
  join_date?: string;
  is_blocked?: boolean;
  dojo_id?: string;
  dojos?: { name: string };
  is_active?: boolean; // Assuming this property exists for StudentStatusToggle
}

interface BeltLevel {
  id: string;
  name: string;
  color: string;
  order_sequence: number;
}

const StudentManagementForm: React.FC = () => {
  useUiLanguage();
  const { profile } = useAuth();
  const { deleteStudent } = useStudentDeletion();
  const { t } = useTranslation(); // Hook for translations

  const [students, setStudents] = useState<Student[]>([]);
  const [beltLevels, setBeltLevels] = useState<BeltLevel[]>([]);
  const [selectedStudent, setSelectedStudent] = useState<string | null>(null); // Explicitly null
  const [dojoFilter, setDojoFilter] = useState<string>('all');
  const [isFormVisible, setIsFormVisible] = useState(false); // State to control form visibility
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false); // State for delete confirmation
  const [isSubmitting, setIsSubmitting] = useState(false); // For form submission state
  const [isLoading, setIsLoading] = useState(false);

  // Placeholder for form data and errors, assuming these are managed elsewhere or for add student functionality
  const [formData, setFormData] = useState<Partial<Student>>({}); // For adding new students
  const [errors, setErrors] = useState({}); // For form validation errors

  const [studentData, setStudentData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    postal_code: '',
    date_of_birth: '',
    belt_level_id: '',
    join_date: ''
  });

  useEffect(() => {
    if (profile) {
      fetchStudents();
      fetchBeltLevels();
    }
  }, [profile?.role, profile?.dojo_id]);

  const fetchStudents = async () => {
    if (!profile) return;

    try {
      let query = supabase
        .from('profiles')
        .select(`
          *,
          dojos!profiles_dojo_id_fkey(name)
        `)
        .eq('role', 'student')
        .order('first_name');

      const isSuperUser = profile.role === 'super_user';

      if (!isSuperUser && profile.dojo_id) {
        query = query.eq('dojo_id', profile.dojo_id);
      }

      const { data, error } = await query;

      if (error) {
        console.error('Error fetching students:', error);
        toast({ title: uiText("Error"), description: uiText("Failed to fetch students."), variant: "destructive" });
        return;
      }

      setStudents(data || []);
    } catch (error) {
      console.error('Error fetching students:', error);
      toast({ title: uiText("Error"), description: uiText("An unexpected error occurred while fetching students."), variant: "destructive" });
    }
  };

  const fetchBeltLevels = async () => {
    try {
      const { data, error } = await supabase
        .from('belt_levels')
        .select('*')
        .order('order_sequence');

      if (error) {
        console.error('Error fetching belt levels:', error);
        toast({ title: uiText("Error"), description: uiText("Failed to fetch belt levels."), variant: "destructive" });
        return;
      }

      setBeltLevels(data || []);
    } catch (error) {
      console.error('Error fetching belt levels:', error);
      toast({ title: uiText("Error"), description: uiText("An unexpected error occurred while fetching belt levels."), variant: "destructive" });
    }
  };

  const getStudentBelt = (beltLevelId?: string) => {
    return beltLevels.find(belt => belt.id === beltLevelId);
  };

  useEffect(() => {
    if (selectedStudent) loadStudentData(selectedStudent);
  }, [selectedStudent, students]);

  const handleDeleteStudent = async () => {
    if (!selectedStudent) {
      toast({
        title: uiText("No Student Selected"),
        description: uiText("Please select a student to delete."),
        variant: "destructive"
      });
      return;
    }

    const student = students.find(s => s.id === selectedStudent);
    if (!student) return;

    console.log('Admin Profile Manager - MASTER DELETE initiated for student:', selectedStudent);
    setIsLoading(true);
    try {
      await deleteStudent(selectedStudent, `${student.first_name} ${student.last_name}`);
      toast({ title: uiText("Student Deleted"), description: uiText("{{value0}} {{value1}} has been successfully deleted.", { value0: student.first_name, value1: student.last_name }) });
      await fetchStudents();
      setSelectedStudent(null);
      setStudentData({
        first_name: '', last_name: '', email: '', phone: '', address: '',
        city: '', postal_code: '', date_of_birth: '', belt_level_id: '', join_date: ''
      });
      setShowDeleteConfirmation(false);
    } catch (error) {
      console.error("Error deleting student:", error);
      toast({ title: uiText("Deletion Failed"), description: uiText("Could not delete the student."), variant: "destructive" });
    } finally {
      setIsLoading(false);
    }
  };

  const handleBlockStudent = async () => {
    if (!selectedStudent) {
      toast({ title: uiText("No Student Selected"), description: uiText("Please select a student to block."), variant: "destructive" });
      return;
    }

    const student = students.find(s => s.id === selectedStudent);
    if (!student) return;

    setIsLoading(true);

    try {
      const { error } = await supabase
        .from('profiles')
        .update({ is_blocked: true })
        .eq('id', selectedStudent);

      if (error) {
        toast({ title: uiText("Block Failed"), description: error.message, variant: "destructive" });
        return;
      }

      toast({ title: uiText("Student Blocked"), description: uiText("{{value0}} {{value1}} has been blocked.", { value0: student.first_name, value1: student.last_name }) });
      await fetchStudents();
    } catch (error) {
      toast({ title: uiText("Error"), description: uiText("An unexpected error occurred while blocking the student."), variant: "destructive" });
    } finally {
      setIsLoading(false);
    }
  };

  const handleUnblockStudent = async () => {
    if (!selectedStudent) {
      toast({ title: uiText("No Student Selected"), description: uiText("Please select a student to unblock."), variant: "destructive" });
      return;
    }

    const student = students.find(s => s.id === selectedStudent);
    if (!student) return;

    setIsLoading(true);

    try {
      const { error } = await supabase
        .from('profiles')
        .update({ is_blocked: false })
        .eq('id', selectedStudent);

      if (error) {
        toast({ title: uiText("Unblock Failed"), description: error.message, variant: "destructive" });
        return;
      }

      toast({ title: uiText("Student Unblocked"), description: uiText("{{value0}} {{value1}} has been unblocked.", { value0: student.first_name, value1: student.last_name }) });
      await fetchStudents();
    } catch (error) {
      toast({ title: uiText("Error"), description: uiText("An unexpected error occurred while unblocking the student."), variant: "destructive" });
    } finally {
      setIsLoading(false);
    }
  };

  const handleStudentUpdate = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!selectedStudent) {
      toast({ title: uiText("No Student Selected"), description: uiText("Please select a student to update."), variant: "destructive" });
      return;
    }

    setIsLoading(true);

    try {
      const { error } = await supabase
        .from('profiles')
        .update({
          first_name: studentData.first_name,
          last_name: studentData.last_name,
          email: studentData.email,
          phone: studentData.phone,
          address: studentData.address,
          city: studentData.city,
          postal_code: studentData.postal_code,
          date_of_birth: studentData.date_of_birth || null,
          belt_level_id: studentData.belt_level_id || null,
          join_date: studentData.join_date
        })
        .eq('id', selectedStudent);

      if (error) {
        toast({ title: uiText("Update Failed"), description: error.message, variant: "destructive" });
        return;
      }

      toast({ title: uiText("Student Updated"), description: uiText("Student data has been successfully updated.") });
      await fetchStudents();
    } catch (error) {
      toast({ title: uiText("Error"), description: uiText("An unexpected error occurred."), variant: "destructive" });
    } finally {
      setIsLoading(false);
    }
  };

  const loadStudentData = (studentId: string) => {
    const student = students.find(s => s.id === studentId);
    if (student) {
      setStudentData({
        first_name: student.first_name,
        last_name: student.last_name,
        email: student.email,
        phone: student.phone || '',
        address: student.address || '',
        city: student.city || '',
        postal_code: student.postal_code || '',
        date_of_birth: student.date_of_birth || '',
        belt_level_id: student.belt_level_id || '',
        join_date: student.join_date || ''
      });
    }
  };

  const handleStudentChange = (studentId: string) => {
    setSelectedStudent(studentId);
    if (studentId) {
      loadStudentData(studentId);
      setIsFormVisible(false); // Hide form when selecting a new student
    }
  };

  const selectedStudentData = students.find(s => s.id === selectedStudent) || null; // Handle null case
  const selectedStudentBelt = selectedStudentData ? getStudentBelt(selectedStudentData.belt_level_id) : null;

  const filteredStudents = profile?.role === 'super_user' && dojoFilter !== 'all'
    ? students.filter(s => s.dojo_id === dojoFilter)
    : students;

  const availableDojos = Array.from(new Set(students.map(s => s.dojos?.name).filter(Boolean)))
    .map(name => ({ id: students.find(s => s.dojos?.name === name)?.dojo_id || '', name }));

  // Mock handlers for StudentActions and StudentStatusToggle if they are not defined in this file
  const handleStudentUpdateFromActions = (updatedStudentData: Partial<Student>) => {
    // This would typically trigger the main handleStudentUpdate or a dedicated update function
    // For now, let's assume it directly updates the data that handleStudentUpdate uses
    setStudentData(prev => ({ ...prev, ...updatedStudentData }));
    // To actually save, you'd need to call handleStudentUpdate or similar here
    // For demonstration, we'll simulate a save by directly updating the student data state
    // and then calling handleStudentUpdate in the next tick
    if (selectedStudent) {
      setTimeout(() => handleStudentUpdate(new Event('submit') as React.FormEvent), 0);
    }
  };

  const handleStatusChange = async (studentId: string, newStatus: boolean) => {
    setIsLoading(true);
    try {
      const { error } = await supabase
        .from('profiles')
        .update({ is_active: newStatus })
        .eq('id', studentId);

      if (error) {
        toast({ title: uiText("Status Update Failed"), description: error.message, variant: "destructive" });
        return;
      }

      toast({ title: uiText("Status Updated"), description: uiText("Student status updated successfully.") });
      await fetchStudents(); // Refresh list
    } catch (error) {
      toast({ title: uiText("Error"), description: uiText("An unexpected error occurred while updating status."), variant: "destructive" });
    } finally {
      setIsLoading(false);
    }
  };

  // Mock handler for handleSubmit for adding a new student
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    // Logic to add a new student using formData
    // For now, just log and reset
    console.log("Adding student with data:", formData);
    toast({ title: uiText("Student Added"), description: uiText("New student has been added.") });
    setIsFormVisible(false);
    setFormData({});
    setIsSubmitting(false);
    await fetchStudents(); // Refresh list
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (name: string, value: string) => {
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  return (
    <Card className="shadow-lg border-0">
      <CardHeader className="bg-gradient-to-r from-indigo-50 to-indigo-100 p-3 sm:p-6">
        <CardTitle className="text-indigo-800 flex items-center gap-2 text-base sm:text-lg">
          <Users className="h-4 w-4 sm:h-5 sm:w-5" />
          {t('admin:studentManagement')}
        </CardTitle>
        <CardDescription className="text-indigo-600 text-sm">{t('admin:studentManagementDescription')}</CardDescription>
      </CardHeader>
      <CardContent className="p-3 sm:p-6">
        <div className="space-y-3 sm:space-y-6">
          {/* Student Selector */}
          <div className="space-y-3 sm:space-y-4">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
              <h3 className="text-base sm:text-lg font-semibold text-gray-800">{t('admin:selectStudent')}</h3>
              <div className="flex flex-col sm:flex-row gap-2">
                <AddManagedStudentDialog
                  beltLevels={beltLevels}
                  onCreated={async (studentId) => {
                    await fetchStudents();
                    setSelectedStudent(studentId);
                  }}
                />
                <Button
                  onClick={() => setShowDeleteConfirmation(true)}
                  disabled={!selectedStudent}
                  variant="destructive"
                  size="sm"
                  className="w-full sm:w-auto text-xs sm:text-sm"
                >
                  <Trash2 className="h-3 w-3 sm:h-4 sm:w-4 mr-1 sm:mr-2" />
                  {uiText("Delete")}</Button>
                {/* Removed Add Student button as per request */}
              </div>
            </div>
            {!isFormVisible && (
              <StudentSelector
                students={filteredStudents}
                beltLevels={beltLevels}
                selectedStudent={selectedStudent}
                onStudentChange={handleStudentChange}
                loadStudentData={loadStudentData}
              />
            )}
          </div>

          {/* Student Details */}
          {selectedStudent && selectedStudentData && !isFormVisible && (
            <div className="mt-3 sm:mt-6 p-3 sm:p-4 bg-gray-50 rounded-lg">
              <h4 className="text-sm sm:text-md font-semibold text-gray-800 mb-3 sm:mb-4">{t('admin:studentDetails')}</h4>
              <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
                <div className="space-y-3">
                  <StudentDetailsView studentId={selectedStudentData!.id} />
                </div>
                <form className="space-y-4" onSubmit={handleStudentUpdate}>
                  <StudentFormFields studentData={studentData} setStudentData={setStudentData} beltLevels={beltLevels} selectedStudentBelt={selectedStudentBelt} />
                  <StudentActions
                    selectedStudent={selectedStudent}
                    selectedStudentData={selectedStudentData!}
                    isLoading={isLoading}
                    onUpdate={handleStudentUpdate}
                    onDelete={handleDeleteStudent}
                    onBlock={handleBlockStudent}
                    onUnblock={handleUnblockStudent}
                  />
                  <StudentStatusToggle
                    studentId={selectedStudentData!.id}
                    currentStatus={selectedStudentData!.is_active!}
                    onStatusChange={() => fetchStudents()}
                  />
                </form>
              </div>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default StudentManagementForm;
